import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, CheckCircle2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0F1418] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";

export default function ReviewModal({ open, onClose, packages }) {
  const [form, setForm] = useState({ name: "", from_place: "", trip: "", text: "", rating: 5 });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/reviews", { ...form, rating: Number(form.rating) });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setError("");
      setForm({ name: "", from_place: "", trip: "", text: "", rating: 5 });
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="review-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl border border-white/10 bg-[#171F26] p-8 sm:rounded-3xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]">Share your journey</p>
                <h3 className="mt-2 font-serif text-2xl font-semibold">Write a review</h3>
              </div>
              <button data-testid="review-close" onClick={close} aria-label="Close review form" className="rounded-full border border-white/15 p-2 text-white/60 transition-colors hover:border-[#D4AF37] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <div className="mt-8 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#D4AF37]" />
                <h4 className="mt-4 font-serif text-xl font-semibold">Thank you!</h4>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Your review is with our team for a quick check and will appear on the site shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="flex justify-center gap-2" role="radiogroup" aria-label="Star rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onClick={() => setForm({ ...form, rating: n })}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      className="transition-transform hover:scale-110"
                    >
                      <Star className={`h-8 w-8 ${n <= form.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/25"}`} />
                    </button>
                  ))}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="review-name-input" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <input data-testid="review-place-input" placeholder="Your city" value={form.from_place} onChange={(e) => setForm({ ...form, from_place: e.target.value })} className={inputCls} />
                </div>
                <select data-testid="review-trip-select" value={form.trip} onChange={(e) => setForm({ ...form, trip: e.target.value })} className={inputCls}>
                  <option value="">Which trip was it? (optional)</option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                  <option value="Custom trip">Custom trip</option>
                </select>
                <textarea data-testid="review-text-input" required rows="3" placeholder="How was the journey?" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className={inputCls} />
                {error && (
                  <p data-testid="review-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">{error}</p>
                )}
                <button
                  type="submit"
                  data-testid="review-submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Submit Review"}
                </button>
                <p className="text-center text-xs text-white/40">Reviews are published after a quick moderation check.</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
